/* eslint-disable semi */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import Helmet from 'react-helmet';
import Script from './script';
import Style from './style';
import Noscript from './noscript';
import CriticalStyles from './critical_css';
import DevCss from './dev_css';

function ClientSideHead(initialPageLoad, attr) {
  if (!initialPageLoad) {
    return <Helmet {...attr} />;
  }
  return null;
}

function PreloadJS({ props }) {
  const { env } = props;

  if (env === 'development' || 'production') {
    return [
      <link
        key="moatPrebid"
        rel="preload"
        href="https://z.moatads.com/mansuetoprebidheader15141606144/yi.js"
        as="script"
      />,

      <link
        key="moatads"
        rel="preconnect"
        href="https://mb.moatads.com"
        crossOrigin="true"
      />,

      <link
        key="prebid"
        rel="preload"
        href="https://assets.fastcompany.com/assets/js/vendor/prebid/prebid_2.39.0.js"
        as="script"
      />,

      <link
        key="gpt"
        rel="preload"
        href="https://www.googletagservices.com/tag/js/gpt.js"
        as="script"
      />,

      <link
        key="vendor"
        rel="preload"
        href={`${props.manifestJSON['vendor.js']}`}
        as="script"
      />,

      <link
        key="app"
        rel="preload"
        href={`${props.manifestJSON['app.js']}`}
        as="script"
      />
    ];
  }

  if (env === 'test') {
    return [
      <link
        key="gpt"
        rel="preload"
        href="https://www.googletagservices.com/tag/js/gpt.js"
        as="script"
      />,

      <link
        key="vendor"
        rel="preload"
        href={`/js/${props.manifestJSON['vendor.js']}`}
        as="script"
      />,

      <link
        key="app"
        rel="preload"
        href={`/js/${props.manifestJSON['app.js']}`}
        as="script"
      />
    ];
  }
  return null;
}

function Head(props) {
  const meta = get(props, 'meta.meta') || [];
  const link = get(props, 'meta.link') || [];
  const script = get(props, 'meta.script') || [];
  const noscript = get(props, 'meta.noscript') || [];
  const style = get(props, 'meta.style') || [];
  const title = get(props, 'meta.title');
  const staticPath =
    'https://images.fastcompany.net/multisite_files/fastcompany/static/sites/fonts/201010';

  // meta.push({'httpEquiv': 'Content-Type', 'content': 'text/html; charset=utf-8'});
  // meta.push({ charSet: 'UTF-8' });

  // On initial render Helmet has a bug: https://github.com/nfl/react-helmet/issues/98
  return (
    <head
      prefix="og: http://ogp.me/ns# fb: http://ogp.me/ns/fb# article: http://ogp.me/ns/article#"
      lang="en"
    >
      <meta charSet="utf-8" />
      <title>{title}</title>

      {meta.map(function createMetaTags(conf) {
        return <meta key={JSON.stringify(conf)} {...conf} />;
      })}
      <link rel="dns-prefetch" href={props.staticUrl} />
      <PreloadJS props={props} />
      <script
        src="https://assets.fastcompany.com/assets/js/vendor/prebid/prebid_2.39.0.js"
        async
      />
      <script
        src="https://z.moatads.com/mansuetoprebidheader15141606144/yi.js"
        async
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/Beckett/A2Beckett-BookWEB.woff`}
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/CentraNo1-Bold-Italic-Web/CentraNo1-Bold.woff`}
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/CentraNo1-Bold-Italic-Web/CentraNo1-BoldItalic.woff`}
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/CentraNo1-Book-Italic-Web/CentraNo1-Book.woff`}
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/CentraNo1-Book-Italic-Web/CentraNo1-BookItalic.woff`}
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/Grifito-Grifo/Grifito-Web/Grifito-L-Bold.woff`}
      />
      <link
        rel="preload"
        as="font"
        crossOrigin="crossorigin"
        type="font/woff"
        href={`${staticPath}/Grifito-Grifo/Grifito-Web/Grifito-L-Bold-Italic.woff`}
      />
      <CriticalStyles />
      <DevCss />

      {/* <script src="https://www.googletagservices.com/tag/js/gpt.js" async /> */}

      {link.map(function createLinkTags(conf) {
        return <link key={JSON.stringify(conf)} {...conf} />;
      })}

      {script.map(function createScriptTags(conf) {
        return <Script key={JSON.stringify(conf)} {...conf} />;
      })}

      {noscript.map(function createNoScriptTags(conf) {
        return <Noscript key={JSON.stringify(conf)} {...conf} />;
      })}

      {style.map(function createStyleTags(conf) {
        return <Style key={JSON.stringify(conf)} {...conf} />;
      })}
      {ClientSideHead(props.initialPageLoad, {
        title,
        meta,
        link,
        script,
        noscript,
        style
      })}
    </head>
  );
}

function mapStateToProps(state) {
  return {
    env: state.config.env,
    staticUrl: state.config.staticUrl,
    manifestJSON: state.config.manifestJSON,
    staticVendorUrl: state.config.staticVendorUrl,
    staticBundleUrl: state.config.staticBundleUrl,
    meta: state.meta,
    initialPageLoad: state.config.initialPageLoad
  };
}

Head.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  env: PropTypes.string.isRequired,
  staticUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  manifestJSON: PropTypes.shape({
    'vendor.js': PropTypes.string,
    'app.js': PropTypes.string
  }).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  staticBundleUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  staticVendorUrl: PropTypes.string.isRequired,
  initialPageLoad: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  meta: PropTypes.shape({}).isRequired
};

export default connect(mapStateToProps)(Head);
